/**
 * Presentation helpers.
 *
 * Money arriving from the API is always an integer count of minor units. These
 * functions turn it into text for display. Nothing here performs arithmetic on
 * money for any purpose that matters: every authoritative figure is computed by
 * the server. Where a total is summed here it is summed from integers the
 * server supplied, and only ever to display a subtotal the user is already
 * looking at.
 */

/** Format integer minor units as UK currency: 123456 -> "£1,234.56". */
export function money(minor: number | null | undefined, options: { showSymbol?: boolean; blankIfZero?: boolean } = {}): string {
  if (minor === null || minor === undefined) return '—';
  if (options.blankIfZero && minor === 0) return '';
  const { showSymbol = true } = options;
  const negative = minor < 0;
  const abs = Math.abs(minor);
  const whole = Math.floor(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const pence = (abs % 100).toString().padStart(2, '0');
  return `${negative ? '−' : ''}${showSymbol ? '£' : ''}${whole}.${pence}`;
}

/** Compact form for dashboard tiles: "£1.2m", "£847k". */
export function moneyCompact(minor: number | null | undefined): string {
  if (minor === null || minor === undefined) return '—';
  const abs = Math.abs(minor);
  const sign = minor < 0 ? '−' : '';
  if (abs >= 100_000_000) return `${sign}£${(abs / 100_000_000).toFixed(abs >= 1_000_000_000 ? 1 : 2)}m`;
  if (abs >= 100_000) return `${sign}£${Math.round(abs / 100_000)}k`;
  return money(minor);
}

/**
 * A plain calendar date, UK style: "2027-06-01" -> "01/06/2027".
 *
 * DD/MM/YYYY is the default everywhere, deliberately. A finance screen that
 * shows "1 Jun 2027" in one column and "01/06/2027" in the next looks unfinished,
 * and a management pack that mixes the two invites the reader to wonder which
 * one is the real date. The long forms remain available for prose — a report
 * heading reads better as "as at 30 September 2026" — but they are asked for
 * explicitly, never arrived at by default.
 */
export function date(value: string | null | undefined, style: 'short' | 'long' | 'numeric' = 'numeric'): string {
  if (!value) return '—';
  const iso = value.slice(0, 10);
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return value;
  if (style === 'numeric') return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
  const months = style === 'long'
    ? ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d} ${months[m - 1]} ${y}`;
}

/** An instant in UK local time: "01/06/2027, 14:32". */
export function dateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(dt);
}

/** Just the clock part of an instant, for an audit timeline: "10:42". */
export function timeOfDay(value: string | null | undefined): string {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(dt);
}

/**
 * Elapsed time in plain words: "3 days ago", "just now".
 *
 * A FUTURE instant is handed to `dueIn` rather than being described as elapsed.
 * Without that guard everything ahead of now fell into the "less than a minute"
 * branch and read "just now": every approval due date, and both service-level
 * clocks on every open support ticket, which are future by definition.
 */
export function relativeTime(value: string | null | undefined): string {
  if (!value) return '—';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return String(value);
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 0) return dueIn(value);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 31) return `${days} day${days === 1 ? '' : 's'} ago`;
  return date(new Date(then).toISOString());
}

/**
 * How long until a deadline, in the words somebody chasing it would use.
 *
 * Deliberately separate from `relativeTime`: "in 3 days" and "3 days ago" are
 * opposite facts, and a queue that renders a deadline as though it had already
 * happened is worse than one that shows a raw date.
 */
export function dueIn(value: string | null | undefined): string {
  if (!value) return '—';
  const at = new Date(value).getTime();
  if (Number.isNaN(at)) return String(value);
  const seconds = Math.round((at - Date.now()) / 1000);
  if (seconds < 0) {
    const overdue = relativeTime(value);
    return overdue === 'just now' ? 'due now' : `overdue by ${overdue.replace(/ ago$/, '')}`;
  }
  if (seconds < 3600) {
    const minutes = Math.max(1, Math.round(seconds / 60));
    return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const hours = Math.round(seconds / 3600);
  if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'tomorrow';
  if (days < 31) return `in ${days} days`;
  return `by ${date(new Date(at).toISOString())}`;
}

export function percent(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(decimals)}%`;
}

export function number(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-GB');
}

/** Turn a snake_case or machine value into readable text: "part_received" -> "Part received". */
export function humanise(value: string | null | undefined): string {
  if (!value) return '—';
  const spaced = value.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export type BadgeTone = 'neutral' | 'ok' | 'warn' | 'error' | 'info' | 'brand';

/**
 * Map a document status to a badge tone.
 *
 * Tone is never the only signal — every badge also carries its label, and the
 * error tone additionally uses a square marker, so the state is legible without
 * relying on colour perception.
 */
export function statusTone(status: string | null | undefined): BadgeTone {
  if (!status) return 'neutral';
  const s = status.toLowerCase();
  // 'pass' is a budget check that found the money — the good outcome, and it
  // was falling through to neutral, so the only unstyled state on the screen
  // was the one everybody is looking for.
  if (['posted', 'paid', 'approved', 'active', 'matched', 'completed', 'settled', 'reconciled', 'balanced', 'succeeded', 'healthy', 'complete', 'imported', 'received', 'invoiced', 'verified', 'closed', 'pass',
    // Settled states from the sub-ledgers: a goods receipt that is confirmed,
    // an extracted contract term a person accepted, an asset in service.
    'confirmed', 'accepted', 'in_use'].includes(s)) return 'ok';
  if (['draft', 'proposed', 'future', 'not_run', 'uncoded', 'planning', 'staged', 'open'].includes(s)) return 'neutral';
  if (['pending_approval', 'in_progress', 'matching', 'coding', 'part_received', 'part_invoiced', 'part_paid', 'partially_relieved', 'awaiting_review', 'pending_verification', 'issued', 'acknowledged', 'submitted', 'calculated', 'degraded', 'partial', 'retrying', 'closing', 'validated', 'mapped', 'dry_run', 'file_generated',
    // A reviewer's amendment, and an asset still being built.
    'amended', 'under_construction'].includes(s)) return 'info';
  // 'overridden' is a budget block that a person deliberately waived — a state
  // that should catch the eye rather than read as ordinary.
  if (['discrepancy', 'warn', 'warning', 'on_hold', 'overdue', 'disputed', 'out_of_balance', 'medium', 'blocked', 'outstanding', 'inspected', 'uploaded', 'overridden',
    // States that need somebody to act before they become a problem.
    'expiring', 'impaired', 'held_for_disposal', 'fully_depreciated'].includes(s)) return 'warn';
  if (['rejected', 'cancelled', 'failed', 'failing', 'error', 'block', 'critical', 'high', 'void', 'returned', 'written_off', 'permanently_closed', 'reversed',
    // Finished, and not in a good way.
    'expired', 'terminated', 'superseded', 'transferred_out'].includes(s)) return 'error';
  return 'neutral';
}

/** A plain-English label for a status, avoiding system vocabulary where possible. */
export function statusLabel(status: string | null | undefined): string {
  if (!status) return 'Unknown';
  const map: Record<string, string> = {
    pending_approval: 'Awaiting approval',
    part_received: 'Partly received',
    part_invoiced: 'Partly invoiced',
    part_paid: 'Partly paid',
    file_generated: 'File generated',
    permanently_closed: 'Permanently closed',
    pending_verification: 'Awaiting verification',
    partially_relieved: 'Partly relieved',
    within_tolerance: 'Within tolerance',
    price_variance: 'Price variance',
    quantity_variance: 'Quantity variance',
    no_receipt: 'No goods receipt',
    over_receipt: 'Over receipt',
    no_order_line: 'No matching order line',
    multiple_variance: 'Several variances',
    not_run: 'Not yet run',
    awaiting_review: 'Awaiting review',
    dry_run: 'Dry run complete',
    out_of_balance: 'Out of balance',

    /*
     * Vocabulary that `humanise` gets wrong.
     *
     * Sentence-casing a machine value turns BACS into "Bacs" and OCR into
     * "Ocr local" — small things, but they are the words a finance team uses
     * every day, and getting them wrong on screen is the difference between
     * software written for them and software written near them.
     */
    bacs: 'BACS',
    bacs_standard18: 'BACS Standard 18',
    csv: 'CSV',
    chaps: 'CHAPS',
    faster_payment: 'Faster Payment',
    direct_debit: 'Direct debit',
    ocr_local: 'Scanned and read here',
    ocr_ai: 'Scanned and read by the assistant',
    manual: 'Keyed by hand',
    integration: 'Received from another system',
    migration: 'Brought over at migration',
    two_way: 'Two-way',
    three_way: 'Three-way',
    four_way: 'Four-way',
  };
  return map[status] ?? humanise(status);
}

/** Class name for a numeric cell, so negatives read clearly. */
export function numClass(minor: number | null | undefined): string {
  if (minor === null || minor === undefined) return 'num num--muted';
  if (minor < 0) return 'num num--negative';
  return 'num';
}

/**
 * Sensible utilisation colour for a progress BAR — a filled block.
 *
 * Never apply this to text. The bar classes set a solid background, so a text
 * node wearing one ends up as dark ink on a saturated fill at about 1.9:1,
 * which fails contrast and reads as a stray coloured chip. Use
 * `utilisationTextClass` for the figure beside the bar.
 */
export function utilisationClass(percentUsed: number): string {
  if (percentUsed > 100) return 'progress__bar progress__bar--over';
  if (percentUsed >= 85) return 'progress__bar progress__bar--warn';
  return 'progress__bar';
}

/** The matching colour for the utilisation FIGURE, at AA contrast on a card. */
export function utilisationTextClass(percentUsed: number): string {
  if (percentUsed > 100) return 'util-figure util-figure--over';
  if (percentUsed >= 85) return 'util-figure util-figure--warn';
  return 'util-figure';
}

/** Today as a plain ISO calendar date, for date input defaults. */
export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/**
 * Multiply a decimal quantity by a decimal price and return pence.
 *
 * Used only to show a running line total as the user types. The server
 * recomputes every figure from the same inputs when the document is saved, and
 * the server's figure is the one that counts.
 */
export function previewLineTotal(quantity: string, unitPrice: string): number | null {
  const q = Number(quantity);
  const p = Number(unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return null;
  return Math.round(q * Math.round(p * 100));
}
